import { http } from "../../api/client";

type UploadResponse = {
    url?: string;
};

export type UploadBatchResult = {
    urls: string[];
    failedFiles: File[];
};

export type UploadBatchProgress = {
    fileName: string;
    fileNumber: number;
    totalFiles: number;
    filePercent: number;
    totalPercent: number;
};

export type UploadProgressHandler = (progress: UploadBatchProgress) => void;

const UPLOAD_TIMEOUT_MS = 90000;

function clampPercent(value: number): number {
    return Math.min(100, Math.max(0, Math.round(value)));
}

async function uploadFile(
    file: File,
    onProgress?: (percent: number) => void,
): Promise<string> {
    let highestReportedPercent = 0;

    const formData = new FormData();
    formData.append("file", file);

    const response = await http.post<UploadResponse>("/api/uploads", formData, {
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (event) => {
            const total = event.total ?? file.size;
            if (total <= 0) {
                return;
            }

            highestReportedPercent = Math.max(
                highestReportedPercent,
                clampPercent((event.loaded / total) * 100),
            );
            onProgress?.(highestReportedPercent);
        },
    });
    const url = response.data?.url;
    if (!url) {
        throw new Error("Сервер не вернул ссылку на загруженный файл");
    }

    onProgress?.(100);
    return url;
}

export async function uploadFilesToApiBestEffort(
    files: File[],
    onProgress?: UploadProgressHandler,
): Promise<UploadBatchResult> {
    const urls: string[] = [];
    const failedFiles: File[] = [];

    const reportProgress = (file: File, fileIndex: number, filePercent: number) => {
        const normalizedFilePercent = clampPercent(filePercent);
        const totalPercent = files.length > 0
            ? clampPercent(((fileIndex + normalizedFilePercent / 100) / files.length) * 100)
            : 100;

        onProgress?.({
            fileName: file.name,
            fileNumber: fileIndex + 1,
            totalFiles: files.length,
            filePercent: normalizedFilePercent,
            totalPercent,
        });
    };

    // Upload sequentially: several large phone photos in parallel can saturate
    // a weak salon uplink and used to abort the whole ticket before creation.
    for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
        const file = files[fileIndex];
        reportProgress(file, fileIndex, 0);
        try {
            urls.push(await uploadFile(
                file,
                (filePercent) => reportProgress(file, fileIndex, filePercent),
            ));
        } catch {
            failedFiles.push(file);
            reportProgress(file, fileIndex, 100);
        }
    }

    return { urls, failedFiles };
}

export async function uploadFilesToApi(files: File[]): Promise<string[]> {
    const result = await uploadFilesToApiBestEffort(files);
    if (result.failedFiles.length > 0) {
        throw new Error(`Не удалось загрузить файлов: ${result.failedFiles.length}`);
    }

    return result.urls;
}
